import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Presentation, Trash2, Edit, Play, MoreVertical } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import AdminLayout from '../components/AdminLayout';
import { getSlideshows, createSlideshow, deleteSlideshow } from '../services/api';

const AdminSlideshows = () => {
  const navigate = useNavigate();
  const [slideshows, setSlideshows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadSlideshows();
  }, []);

  const loadSlideshows = async () => {
    try {
      const data = await getSlideshows();
      setSlideshows(data);
    } catch (error) {
      console.error('Error loading slideshows:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    
    setCreating(true);
    try {
      await createSlideshow({
        name: newName,
        description: newDescription,
        is_active: true
      });
      setNewName('');
      setNewDescription('');
      setShowNewForm(false);
      loadSlideshows();
    } catch (error) {
      console.error('Error creating slideshow:', error);
      alert('Error creating slideshow');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this slideshow? This will delete all slides in it.')) {
      return;
    }
    
    try {
      await deleteSlideshow(id);
      loadSlideshows();
    } catch (error) {
      console.error('Error deleting slideshow:', error);
      alert('Error deleting slideshow');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <AdminLayout currentPage="slideshows">
        <div className="text-center py-20">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-400">Loading slideshows...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="slideshows">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-white">Pitch Deck Slideshows</h2>
          <p className="text-slate-400 text-sm">Create and manage presentation slideshows for pitch events</p>
        </div>
        <Button
          onClick={() => setShowNewForm(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Slideshow
        </Button>
      </div>

      {/* New Slideshow Form */}
      {showNewForm && (
        <Card className="mb-6 bg-slate-900 border-blue-500/30">
          <CardContent className="p-6">
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Slideshow Name
                </label>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g., Q1 2026 Pitch Event"
                  className="bg-slate-800 border-slate-700 text-white"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Description (optional)
                </label>
                <Input
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="Brief description of this slideshow"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={!newName.trim() || creating}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {creating ? 'Creating...' : 'Create Slideshow'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowNewForm(false);
                    setNewName('');
                    setNewDescription('');
                  }}
                  className="border-slate-600 text-slate-300"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Slideshows Grid */}
      {slideshows.length === 0 ? (
        <div className="text-center py-20 bg-slate-900/50 rounded-lg border border-slate-800">
          <Presentation className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Slideshows Yet</h3>
          <p className="text-slate-400 mb-4">Create your first pitch deck slideshow to get started</p>
          <Button
            onClick={() => setShowNewForm(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Slideshow
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {slideshows.map((slideshow) => (
            <Card 
              key={slideshow._id} 
              className="bg-slate-900 border-slate-800 hover:border-slate-700 transition-all group cursor-pointer"
              onClick={() => navigate(`/admin/slideshows/${slideshow._id}`)}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                    <Presentation className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-slate-400 hover:text-white h-8 w-8 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/slideshows/${slideshow._id}/present`);
                      }}
                      title="Present"
                    >
                      <Play className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-slate-400 hover:text-red-400 h-8 w-8 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(slideshow._id);
                      }}
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <h3 className="font-semibold text-white mb-1 line-clamp-1">
                  {slideshow.name}
                </h3>
                {slideshow.description && (
                  <p className="text-sm text-slate-400 mb-3 line-clamp-2">
                    {slideshow.description}
                  </p>
                )}
                
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>{slideshow.slide_count || 0} slides</span>
                  <span>{formatDate(slideshow.created_at)}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSlideshows;
